import React, { useMemo, useState } from "react";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { leadStatuses } from "../../data/procrmData";

function LeadTracker({ leads, setLeads }) {
  const [search, setSearch] = useState("");
  const [savingLeadId, setSavingLeadId] = useState("");
  const [popup, setPopup] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
  });

  const showPopup = (type, title, message) => {
    setPopup({
      show: true,
      type,
      title,
      message,
    });
  };

  const closePopup = () => {
    setPopup({
      show: false,
      type: "success",
      title: "",
      message: "",
    });
  };

  const filteredLeads = useMemo(() => {
    const cleanSearch = search.toLowerCase().trim();

    if (!cleanSearch) return leads;

    return leads.filter((lead) => {
      const value = [
        lead.business,
        lead.industry,
        lead.location,
        lead.contact,
        lead.website,
        lead.social,
        lead.problem,
        lead.service,
        lead.status,
        lead.priority,
        lead.followUpDate,
        lead.notes,
      ]
        .join(" ")
        .toLowerCase();

      return value.includes(cleanSearch);
    });
  }, [leads, search]);

  const updateLeadField = async (leadId, fieldName, newValue) => {
    if (!leadId) {
      showPopup(
        "error",
        "Missing lead ID",
        "This lead does not have a Firestore document ID, so it cannot be updated."
      );
      return;
    }

    const previousLeads = leads;

    try {
      setSavingLeadId(leadId);

      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === leadId
            ? {
                ...lead,
                [fieldName]: newValue,
                updatedAt: new Date(),
              }
            : lead
        )
      );

      await updateDoc(doc(db, "leads", leadId), {
        [fieldName]: newValue,
        updatedAt: serverTimestamp(),
      });

      showPopup(
        "success",
        "Lead updated",
        "Your change has been saved to the database."
      );
    } catch (error) {
      console.error("Error updating lead:", error);

      setLeads(previousLeads);

      showPopup(
        "error",
        "Update failed",
        "The lead could not be updated. Please check your Firebase rules or connection."
      );
    } finally {
      setSavingLeadId("");
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "Not set";

    if (typeof dateValue === "string") return dateValue;

    if (dateValue?.toDate) {
      return dateValue.toDate().toLocaleDateString("en-ZA");
    }

    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString("en-ZA");
    }

    return "Not set";
  };

  return (
    <section className="procrm-panel crm-section lead-tracker-section">
      {popup.show && (
        <div className="crm-popup-backdrop">
          <div className={`crm-popup-card ${popup.type}`}>
            <div className="crm-popup-logo-wrap">
              <img src="/linkhighlight.png" alt="ProCode ZA" />
            </div>

            <h3>{popup.title}</h3>
            <p>{popup.message}</p>

            <button type="button" onClick={closePopup}>
              Okay
            </button>
          </div>
        </div>
      )}

      <div className="panel-heading table-heading lead-tracker-heading">
        <div>
          <span className="eyebrow">Pipeline</span>
          <h2>Lead tracker</h2>
          <p className="tracker-subtitle">
            Manage live leads, update their progress, and keep your CRM synced
            with Firestore.
          </p>
        </div>

        <div className="tracker-search-wrap">
          <input
            className="crm-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search leads, industry, problem, status..."
          />
        </div>
      </div>

      <div className="tracker-summary-row">
        <div>
          <span>Total Leads</span>
          <strong>{leads.length}</strong>
        </div>

        <div>
          <span>Showing</span>
          <strong>{filteredLeads.length}</strong>
        </div>

        <div>
          <span>High Priority</span>
          <strong>
            {
              leads.filter(
                (lead) => String(lead.priority).toLowerCase() === "high"
              ).length
            }
          </strong>
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <div className="lead-empty-state">
          <img src="/linkhighlight.png" alt="ProCode ZA" />
          <h3>No leads found</h3>
          <p>
            Add your first lead or adjust your search to see matching businesses
            here.
          </p>
        </div>
      ) : (
        <div className="lead-table-wrap">
          <table className="lead-table">
            <thead>
              <tr>
                <th>Business</th>
                <th>Industry</th>
                <th>Location</th>
                <th>Contact</th>
                <th>Website / Social</th>
                <th>Problem</th>
                <th>Solution</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Follow-Up</th>
              </tr>
            </thead>

            <tbody>
              {filteredLeads.map((lead) => (
                <tr key={lead.id || lead.business}>
                  <td>
                    <div className="lead-business-cell">
                      <strong>{lead.business || "Unnamed lead"}</strong>
                      <span>{lead.notes || "No notes added"}</span>
                    </div>
                  </td>

                  <td>{lead.industry || "Not added"}</td>
                  <td>{lead.location || "Not added"}</td>
                  <td>{lead.contact || "Not added"}</td>

                  <td>
                    <div className="lead-link-stack">
                      {lead.website ? (
                        <a
                          href={
                            lead.website.startsWith("http")
                              ? lead.website
                              : `https://${lead.website}`
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          Website
                        </a>
                      ) : (
                        <span>No website</span>
                      )}

                      {lead.social ? (
                        <a
                          href={
                            lead.social.startsWith("http")
                              ? lead.social
                              : `https://${lead.social}`
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          Social
                        </a>
                      ) : (
                        <span>No social</span>
                      )}
                    </div>
                  </td>

                  <td>{lead.problem || "Not added"}</td>
                  <td>{lead.service || "Not added"}</td>

                  <td>
                    <select
                      className="status-select"
                      value={lead.status || "Not Contacted"}
                      disabled={savingLeadId === lead.id}
                      onChange={(event) =>
                        updateLeadField(lead.id, "status", event.target.value)
                      }
                    >
                      {leadStatuses.map((status) => (
                        <option key={status}>{status}</option>
                      ))}
                    </select>
                  </td>

                  <td>
                    <select
                      className={`priority-select ${String(
                        lead.priority || "Medium"
                      ).toLowerCase()}`}
                      value={lead.priority || "Medium"}
                      disabled={savingLeadId === lead.id}
                      onChange={(event) =>
                        updateLeadField(lead.id, "priority", event.target.value)
                      }
                    >
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                    </select>
                  </td>

                  <td>
                    <input
                      className="followup-date-input"
                      type="date"
                      value={lead.followUpDate || ""}
                      disabled={savingLeadId === lead.id}
                      onChange={(event) =>
                        updateLeadField(
                          lead.id,
                          "followUpDate",
                          event.target.value
                        )
                      }
                    />

                    {!lead.followUpDate && (
                      <span className="followup-muted">Not set</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default LeadTracker;